<template>
  <HrPageLayout
    title="Applicants"
    subtitle="Website + manual applicants, auto shortlist suggestions"
    icon="📋"
    :breadcrumb-items="[{ label: 'Public Service Board Module' }, { label: 'Applicants' }]"
  >
    <template #header-actions>
      <div class="hr-flex hr-flex-center hr-gap-2">
        <select class="hr-select" style="width: 280px;" v-model="selectedVacancyId" @change="fetchApplicants">
          <option value="">Select vacancy to view applicants</option>
          <option v-for="v in vacancies" :key="v.id" :value="v.id">
            {{ v.title }} ({{ v.status }})
          </option>
        </select>
        <button class="hr-btn hr-btn-secondary" @click="fetchApplicants" :disabled="!selectedVacancyId">
          <i class="bx bx-refresh"></i> Refresh
        </button>
        <button class="hr-btn hr-btn-primary" @click="openAddApplicantModal">
          <i class="bx bx-plus"></i> Add Applicant
        </button>
        <button class="hr-btn hr-btn-success" @click="fetchSuggestions" :disabled="!selectedVacancyId">
          <i class="bx bx-filter-alt"></i> Auto Suggest Shortlist
        </button>
      </div>
    </template>

    <div class="applicants-content">
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-group"></i> Applicants</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="!selectedVacancyId" class="hr-empty-state">
            <i class="bx bx-briefcase"></i>
            <h5>Select a vacancy</h5>
            <p>Choose a vacancy from the dropdown above to view its applicants.</p>
          </div>
          <div v-else-if="loading" class="hr-loading-state">
            <div class="hr-spinner"></div>
            <p>Loading applicants...</p>
          </div>
          <div v-else-if="applicants.length === 0" class="hr-empty-state">
            <i class="bx bx-file"></i>
            <h5>No applicants</h5>
            <p>No applicants have been recorded for this vacancy yet.</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>ID</th>
                  <th>Ethnicity</th>
                  <th>Exp (Years)</th>
                  <th style="text-align:center">Status</th>
                  <th style="text-align:center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(a, i) in applicants" :key="a.id">
                  <td>{{ i + 1 }}</td>
                  <td>{{ a.name }}</td>
                  <td>{{ a.email || '–' }}</td>
                  <td>{{ a.phone || '–' }}</td>
                  <td>{{ a.id_number || '–' }}</td>
                  <td>{{ a.ethnicity || '–' }}</td>
                  <td>{{ a.years_experience || '0' }}</td>
                  <td style="text-align:center">
                    <span class="hr-badge" :class="a.status === 'shortlisted' ? 'hr-badge-info' : 'hr-badge-muted'">
                      {{ a.status || 'Applied' }}
                    </span>
                  </td>
                  <td style="text-align:center">
                    <button class="hr-btn hr-btn-sm hr-btn-info" @click="shortlistApplicant(a)">
                      <i class="bx bx-check"></i> Shortlist
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="hr-chart-card hr-mt-3" v-if="suggestions.length">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-bulb"></i> Suggested for Shortlisting</h5>
        </div>
        <div class="hr-chart-body">
          <div class="hr-table-wrapper">
            <table class="hr-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Fit Score</th>
                  <th>Reasons</th>
                  <th style="text-align:center">Action</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="s in suggestions" :key="'s-'+s.id">
                  <td>{{ s.name }}</td>
                  <td>{{ s.email || '–' }}</td>
                  <td><span class="hr-badge" :class="Number(s.fit_score) >= 60 ? 'hr-badge-success' : 'hr-badge-warning'">{{ s.fit_score }}</span></td>
                  <td>{{ s.fit_reasons || '—' }}</td>
                  <td style="text-align:center">
                    <button class="hr-btn hr-btn-sm hr-btn-primary" @click="shortlistApplicant(s)" :disabled="Number(s.fit_score) < 60">
                      Mark Shortlist
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <ModalWrapper ref="addApplicantModal" title="Add Applicant" size="large">
      <template #body>
        <form @submit.prevent class="applicant-form-grid">
          <input class="hr-input" v-model="form.full_name" placeholder="Name *">
          <input class="hr-input" v-model="form.age" type="number" min="0" placeholder="Age">
          <input class="hr-input" v-model="form.dob" type="date" placeholder="D.O.B">
          <select class="hr-select" v-model="form.gender"><option value="">Gender</option><option>Male</option><option>Female</option><option>Other</option></select>
          <input class="hr-input" v-model="form.ethnicity" placeholder="Ethnicity">
          <input class="hr-input" v-model="form.phone" placeholder="Phone">
          <input class="hr-input" v-model="form.id_number" placeholder="ID">
          <input class="hr-input" v-model="form.county" placeholder="County">
          <input class="hr-input" v-model="form.subcounty" placeholder="Subcounty">
          <input class="hr-input" v-model="form.ward" placeholder="Ward">
          <input class="hr-input" v-model="form.email" placeholder="Email">
          <input class="hr-input" v-model="form.personal_no" placeholder="Personal No (if employee)">
          <input class="hr-input" v-model="form.disability_reg_no" placeholder="Disability Registration No">
          <input class="hr-input" v-model="form.years_experience" type="number" min="0" placeholder="Years of relevant experience">
          <input class="hr-input" v-model="form.highest_academic_qualification" placeholder="Highest academic qualification">
          <textarea class="hr-textarea span2" v-model="form.other_academic_qualifications" rows="2" placeholder="Other academic qualifications"></textarea>
          <textarea class="hr-textarea span2" v-model="form.professional_body_membership" rows="2" placeholder="Professional body membership"></textarea>
          <textarea class="hr-textarea span2" v-model="form.clearance_certificates" rows="2" placeholder="Clearance certificates"></textarea>
          <div class="span2">
            <label class="hr-form-label">Attachment of scanned documents</label>
            <input class="hr-input" type="file" ref="docsInput" @change="onDocumentsChange" accept=".pdf,.zip,.jpg,.jpeg,.png">
          </div>
        </form>
      </template>
      <template #footer>
        <button class="hr-btn hr-btn-secondary" @click="$refs.addApplicantModal.close()">Cancel</button>
        <button class="hr-btn hr-btn-success" @click="saveApplicant">Save Applicant</button>
      </template>
    </ModalWrapper>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import ModalWrapper from "@/components/hrActions/ModalWrapper.vue";
import { authUrl, execute, toast } from "@/api";

export default {
  name: "Applicants",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      vacancies: [],
      selectedVacancyId: "",
      applicants: [],
      suggestions: [],
      form: {
        full_name: "",
        age: "",
        dob: "",
        gender: "",
        ethnicity: "",
        phone: "",
        id_number: "",
        county: "",
        subcounty: "",
        ward: "",
        email: "",
        personal_no: "",
        disability_reg_no: "",
        years_experience: "",
        highest_academic_qualification: "",
        other_academic_qualifications: "",
        professional_body_membership: "",
        clearance_certificates: ""
      },
      documentsFile: null
    };
  },
  mounted() {
    this.fetchVacancies();
  },
  methods: {
    async fetchVacancies() {
      const data = new FormData();
      data.append("function", "getVacancies");
      data.append("search", "");
      data.append("status", "");
      try {
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          const raw = res.data.data?.Records || res.data.data?.vacancies || [];
          this.vacancies = Array.isArray(raw) ? raw : [raw];
        }
      } catch (e) {
        console.error("Failed to load vacancies", e);
        toast.error("Failed to load vacancies");
      }
    },
    async fetchApplicants() {
      if (!this.selectedVacancyId) {
        this.applicants = [];
        return;
      }
      this.loading = true;
      const data = new FormData();
      data.append("function", "getCandidatesForVacancy");
      data.append("vacancyId", this.selectedVacancyId);
      try {
        const res = await execute(data, authUrl);
        if (res.data && res.data.success) {
          this.applicants = res.data.data?.candidates || [];
        } else {
          this.applicants = [];
        }
      } catch (e) {
        console.error("Failed to load applicants", e);
        this.applicants = [];
        toast.error("Failed to load applicants");
      } finally {
        this.loading = false;
      }
    }
    ,
    openAddApplicantModal() {
      if (!this.selectedVacancyId) {
        toast.error("Select vacancy first");
        return;
      }
      this.form = {
        full_name: "", age: "", dob: "", gender: "", ethnicity: "", phone: "", id_number: "",
        county: "", subcounty: "", ward: "", email: "", personal_no: "", disability_reg_no: "",
        years_experience: "", highest_academic_qualification: "", other_academic_qualifications: "",
        professional_body_membership: "", clearance_certificates: ""
      };
      this.documentsFile = null;
      this.$nextTick(() => {
        if (this.$refs.docsInput) this.$refs.docsInput.value = "";
        this.$refs.addApplicantModal?.open();
      });
    },
    onDocumentsChange(e) {
      this.documentsFile = e?.target?.files?.[0] || null;
    },
    async saveApplicant() {
      if (!this.selectedVacancyId) return toast.error("Select vacancy first");
      if (!this.form.full_name) return toast.error("Name is required");
      const fd = new FormData();
      fd.append("function", "addApplicant");
      fd.append("vacancyId", this.selectedVacancyId);
      fd.append("source", "manual");
      Object.keys(this.form).forEach((k) => fd.append(k, this.form[k] || ""));
      if (this.documentsFile) fd.append("documents", this.documentsFile);
      try {
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.$refs.addApplicantModal?.close();
          await this.fetchApplicants();
          toast.success("Applicant added");
        } else {
          toast.error(res.data?.message || "Failed to add applicant");
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to add applicant");
      }
    },
    async fetchSuggestions() {
      if (!this.selectedVacancyId) return;
      const fd = new FormData();
      fd.append("function", "getApplicantSuggestions");
      fd.append("vacancyId", this.selectedVacancyId);
      try {
        const res = await execute(fd, authUrl);
        this.suggestions = res.data?.data?.items || [];
      } catch (e) {
        console.error(e);
        this.suggestions = [];
      }
    },
    async shortlistApplicant(applicant) {
      if (!applicant?.id || !this.selectedVacancyId) return;
      const fd = new FormData();
      fd.append("function", "addToShortlist");
      fd.append("candidateId", applicant.id);
      fd.append("vacancyId", this.selectedVacancyId);
      fd.append("notes", "Marked for shortlisting");
      try {
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          toast.success("Candidate marked for shortlisting");
          await this.fetchApplicants();
        } else {
          toast.error(res.data?.message || "Could not shortlist");
        }
      } catch (e) {
        console.error(e);
        toast.error("Could not shortlist");
      }
    }
  }
};
</script>

<style scoped>
.applicants-content {
  padding-top: 12px;
}
.applicant-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.span2 { grid-column: 1 / -1; }
</style>
